import { useIsFetching } from '@tanstack/react-query';
import { Button, Row, Tooltip, Typography } from '@tempmee/react';
import {
  OutlineHeartIcon,
  OutlineXCircleErrorIcon,
  SolidInformationCircleIcon,
} from '@tempmee/react-icons';
import { TextAreaField } from '~/components/shared/forms';
import { colors } from '@tempmee/tailwind';
import { motion } from 'framer-motion';
import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IJob } from '~/models';
import { UserSubProfession } from '~/enums/UserProfession';
import history from '~/browserHistory';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import {
  FETCH_JOB_ACTION,
  SEND_PROCEDURE_ENDORSEMENT,
  addFavoriteProfessional,
  blockProfessional,
  feedbackSave,
  fetchJob,
  sendProcedureEndorsement,
} from '../actions';
import { createLoadingSelector } from '../api/selectors';
import { CardSpinner, RatingInput } from '../commonComponents';
import { getAverageRating, isGoodRating } from '../commonComponents/CommonFunctions';
import { SelectableChips } from '../commonComponents/SelectableChips';

import BlockUserIcon from '../images/block-user.svg';

import '../themes/global.scss';
import '../themes/job_summary.scss';
import { EducationUtils } from '../utils/EducationUtils';
import { ProfessionalActions } from './EOD/components';
import ProfileSummary from './JobSummary/ProfileSummary';
import { RatingBadgesFeedback } from './JobSummary/RatingBadgesFeedback';
import { RatingFeedbackSuccessHandler } from './JobSummaryRating/components/RatingFeedbackSuccessHandler';
import ConfirmPopup from './shared/ConfirmPopup';

// `procedures` is present on the job object for this flow at runtime but is not declared on
// IJob (it originates from the offer payload), so read it through a local augmentation.
type TJobWithProcedures = IJob & { procedures?: string[] | null };

const JobSummaryRating = () => {
  const params = useParams();
  const jobId = params?.jobId;

  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user.user);
  const job = useAppSelector((state) => state.job.job);
  // `adjustmentsSend` stores adjusted job ids at runtime (ADJUST_HOURS saga dispatches
  // `payload: jobId`), though RootState currently types the slice as IJob[].
  const adjustmentsSend = useAppSelector(
    (state) => state.job.adjustmentsSend,
  ) as unknown as string[];
  const showEodInvitePopup = useAppSelector((state) => state.job.showEodInvitePopup);

  const ratingContainerRef = createRef<HTMLDivElement>();
  const [professionalSkillsRating, setProfessionalSkillsRating] = useState(5);
  const [interpersonalSkillsRating, setInterpersonalSkillsRating] = useState(5);
  const [timeManagementRating, setTimeManagementRating] = useState(5);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showBlockConfirmPopup, setShowBlockConfirmPopup] = useState(false);
  const [showUnableToAddBlockedModal, setShowUnableToAddBlockedModal] = useState(false);
  const [selectedProcedureIndices, setSelectedProcedureIndices] = useState<number[]>([]);
  const [ratingBadges, setRatingBadges] = useState<string[]>([]);
  const [shouldShowHandler, setShouldShowHandler] = useState(showEodInvitePopup ?? false);

  const isAdjustmensSent = adjustmentsSend.some((adjustedJobId) => adjustedJobId === job?.id);
  const isFavorite = job?.offer_final_reciever?.isFavorite;
  const professionalName = [
    job?.offer_final_reciever?.first_name,
    job?.offer_final_reciever?.last_name,
  ].join(' ');

  const actions = [FETCH_JOB_ACTION, SEND_PROCEDURE_ENDORSEMENT];
  const loadingSelector = createLoadingSelector(actions);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  const shouldShowProcedureRating = !!professionalSkillsRating && professionalSkillsRating <= 3;
  const { procedures, offer_final_reciever: offerFinalReceiver } = (job ??
    {}) as TJobWithProcedures;
  const isLoadingEligibleJobs =
    useIsFetching({
      queryKey: ['eligible-jobs-infinite'],
    }) > 0;
  const hasSendFeedbackRef = useRef(false);
  const isButtonDisabled =
    !professionalSkillsRating ||
    !interpersonalSkillsRating ||
    !timeManagementRating ||
    isLoadingEligibleJobs;

  useEffect(() => {
    if (!job || jobId !== job.id) {
      dispatch(fetchJob({ jobId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (jobId === job?.id && job?.isFeedbackSubmited && !showEodInvitePopup) {
      history.replace(`/dashboard/job/${job.id}/details`);
    }
  }, [job, jobId, showEodInvitePopup]);

  useEffect(() => {
    setAverageRating(
      getAverageRating([professionalSkillsRating, interpersonalSkillsRating, timeManagementRating]),
    );
  }, [timeManagementRating, professionalSkillsRating, interpersonalSkillsRating]);

  const sendFeedback = useCallback(
    (showMessage = true) => {
      setShouldShowHandler(false);

      if (hasSendFeedbackRef.current) {
        return;
      }
      hasSendFeedbackRef.current = true;

      let professionalRating: Record<string, number> | null = null;
      if (timeManagementRating || interpersonalSkillsRating || professionalSkillsRating) {
        professionalRating = Object.fromEntries(
          Object.entries({
            timeManagement: timeManagementRating,
            interpersonalSkills: interpersonalSkillsRating,
            professionalSkills: professionalSkillsRating,
          }).filter(([, value]) => value !== undefined && value !== null),
        );
      }

      const data = {
        comment: feedbackMessage,
        hireAgain: 'sure',
        rating: null,
        professionalRating,
        ratingBadges: isGoodRating(averageRating as number) ? ratingBadges : null,
      };

      dispatch(feedbackSave({ data, jobId: job?.id, showMessage }));
    },
    [
      feedbackMessage,
      timeManagementRating,
      interpersonalSkillsRating,
      professionalSkillsRating,
      averageRating,
      ratingBadges,
      job?.id,
      dispatch,
    ],
  );

  const handleSendClick = () => {
    if (
      getIsAllowOfficeToPostProcedure(
        UserSubProfession.getName(job?.subProfession) ?? job?.professionName,
        job?.specialty,
      ) &&
      procedures != null &&
      procedures.length > 0
    ) {
      const endorsement = {
        jobId: job?.id,
        profession: job?.profession,
        specialty: job?.specialty,
        endorsed: (() => {
          if (professionalSkillsRating) {
            if (professionalSkillsRating > 3) {
              return procedures;
            }

            return procedures.filter((_, index) => !selectedProcedureIndices.includes(index));
          }

          return [];
        })(),
        renounced: (() => {
          if (professionalSkillsRating) {
            if (professionalSkillsRating > 3) {
              return [];
            }

            return procedures.filter((_, index) => selectedProcedureIndices.includes(index));
          }

          return [];
        })(),
      };

      if (endorsement.endorsed.length > 0 || endorsement.renounced.length > 0) {
        dispatch(sendProcedureEndorsement({ endorsement, userId: offerFinalReceiver?.id }));
      }
    }

    setShouldShowHandler(true);
  };

  const renderRating = () => (
    <>
      <span className="f-dark f-20" style={{ marginTop: 0 }}>
        <b>Please rate this professional </b>
      </span>
      <Typography
        italic
        color={colors.neutral[500]}
        style={{ marginTop: 10, marginBottom: 40, textAlign: 'center', alignSelf: 'center' }}
      >
        Your rating will help us improve our capability to connect you with high-quality candidates.
        This won&apos;t be shared with the professional.
      </Typography>

      <RatingInput
        title="Professional Skills"
        value={professionalSkillsRating}
        onChange={(value) => setProfessionalSkillsRating(value)}
        direction="row"
      />

      {getIsAllowOfficeToPostProcedure(
        UserSubProfession.getName(job?.subProfession) ?? job?.professionName,
        job?.specialty,
      ) &&
        shouldShowProcedureRating &&
        !!procedures &&
        procedures.length > 0 && (
          <div
            style={{
              marginBottom: 32,
            }}
          >
            <p style={{ margin: '2rem 0', fontSize: 18, color: colors.neutral[600] }}>
              <b>Select any procedure this professional was unable to perform:</b>
            </p>
            <SelectableChips
              style={{ marginBottom: 24 }}
              values={EducationUtils.mapToRemoveProcedurePrefix(procedures)}
              selectedIndices={selectedProcedureIndices}
              onItemPressed={(index) => {
                const indices = (() => {
                  const selected = selectedProcedureIndices.includes(index);
                  const collection = selectedProcedureIndices;
                  const maxSelection = 4;

                  if (selected) {
                    return collection.filter((currentIndex) => currentIndex !== index);
                  }

                  if (!(maxSelection && collection.length >= maxSelection)) {
                    return [...collection, index];
                  }

                  return collection;
                })();

                setSelectedProcedureIndices(indices);
              }}
            />
          </div>
        )}

      <RatingInput
        title="Interpersonal Skills"
        value={interpersonalSkillsRating}
        onChange={(value) => setInterpersonalSkillsRating(value)}
        direction="row"
      />

      <RatingInput
        title="Time Management"
        value={timeManagementRating}
        onChange={(value) => setTimeManagementRating(value)}
        direction="row"
      />
    </>
  );

  const renderProfessionalActions = () => {
    const shouldDisplayFavorite = isGoodRating(averageRating as number);
    const isOnsiteInterview = job?.isOnsiteInterview;
    const finalReceiverId = job?.offer_final_reciever?.id;

    if (isOnsiteInterview) {
      return <ProfessionalActions isGoodRating={shouldDisplayFavorite} />;
    }

    if (shouldDisplayFavorite) {
      // `favoriteProfessionals` holds professional ids at runtime, though IUser types it as IUser[].
      const favoriteProfessionals = (user?.favoriteProfessionals ?? []) as unknown as string[];
      const isUserFavorite = !!finalReceiverId && favoriteProfessionals.includes(finalReceiverId);

      return (
        <>
          {!isUserFavorite ? (
            <motion.div
              key="favorite_container"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Row
                align="center"
                justify="center"
                gap={1}
                style={{ margin: '40px 0 30px', maxWidth: 360 }}
              >
                <h3
                  className="f-16 f-dark"
                  style={{
                    margin: 0,
                    maxWidth: 330,
                    textAlign: 'center',
                  }}
                >
                  Would you like to add this professional to your Favorites List?
                </h3>
                <Tooltip content="By adding this professional to your Favorites List, you may directly request them for open shifts in the future.">
                  <SolidInformationCircleIcon color={colors.neutral[300]} />
                </Tooltip>
              </Row>

              <button
                type="button"
                className="green-button"
                data-cy="add-to-favorites-button"
                style={{
                  alignSelf: 'center',
                  width: 220,
                  margin: 0,
                  backgroundColor: colors.primary[500],
                }}
                onClick={() => {
                  const isBlocked =
                    !!finalReceiverId && user?.blocked_hygienist?.includes(finalReceiverId);

                  if (isBlocked) {
                    setShowUnableToAddBlockedModal(true);
                  } else {
                    dispatch(addFavoriteProfessional(job?.offer_final_reciever?.id));
                  }
                }}
              >
                <OutlineHeartIcon color={colors.shades[0]} />

                <p className="white-text-18" style={{ margin: 0 }}>
                  Add to Favorites
                </p>
              </button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ marginTop: '60px' }}
            >
              <p
                className="f-18"
                style={{
                  margin: 0,
                  color: colors.primary[500],
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                Congrats!
              </p>
              <p style={{ color: colors.neutral[600] }}>
                This professional is now on your Favorites List.
              </p>
            </motion.div>
          )}
        </>
      );
    }

    return (
      <>
        {!(!!finalReceiverId && user?.blocked_hygienist?.includes(finalReceiverId)) ? (
          <motion.div
            key="block_container"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <div>
              <h3
                className="f-16 global_font f-dark"
                style={{
                  margin: '40px 0 30px 0',
                  width: '330px',
                  textAlign: 'center',
                }}
              >
                Would you like to block this professional from working with you again?
                <span style={{ marginLeft: '5px' }}>
                  <Tooltip content="Blocking a professional means that they will not be able to see shifts posted by this office, preventing you from working with them in the future.">
                    <SolidInformationCircleIcon color={colors.neutral[300]} />
                  </Tooltip>
                </span>
              </h3>
            </div>

            <button
              type="button"
              className="green-button"
              data-cy="block-professional-button"
              style={{
                alignSelf: 'center',
                width: 240,
                margin: 0,
                backgroundColor: colors.secondary[500],
              }}
              onClick={() => setShowBlockConfirmPopup(true)}
            >
              <img src={BlockUserIcon} style={{ marginRight: 8 }} alt="block_user_icon" />

              <p className="white-text-18" style={{ margin: 0 }}>
                Block Professional
              </p>
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginTop: '60px' }}
          >
            <p
              className="global_font f-18"
              style={{
                margin: 0,
                color: colors.neutral[600],
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Successfully Blocked
            </p>
            <p className="global_font" style={{ color: colors.neutral[600] }}>
              This professional is now blocked.
            </p>
          </motion.div>
        )}
      </>
    );
  };

  const renderRatingBadgesFeedback = () => {
    const shouldDisplay = isGoodRating(averageRating as number);

    if (!shouldDisplay) {
      return null;
    }

    return (
      <motion.div
        key="rating_badges_feedback_container"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <RatingBadgesFeedback
          selectedBadges={ratingBadges}
          onSelectBadge={(selectedBadge) => {
            const badges = ratingBadges.includes(selectedBadge)
              ? ratingBadges.filter((badge) => badge !== selectedBadge)
              : [...ratingBadges, selectedBadge];

            setRatingBadges(badges);
          }}
        />
      </motion.div>
    );
  };

  if (!job || isLoading) {
    return <CardSpinner />;
  }

  return (
    <>
      <div className="component_container global_font">
        <div style={{ width: '100%' }}>
          <ProfileSummary title="Rate Professional" job={job} noBack={isAdjustmensSent} />

          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '-1px 20px 10px 20px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    backgroundColor: colors.shades[0],
                    paddingBlock: 62,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow:
                      '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <div
                    ref={ratingContainerRef}
                    style={{
                      maxWidth: 658,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {renderRating()}
                    {renderProfessionalActions()}
                    {renderRatingBadgesFeedback()}

                    <h3 className="f-18 f-dark" style={{ marginTop: '60px' }}>
                      Confidential Feedback for GoTu{' '}
                      <span className="f-16" style={{ fontWeight: 400, color: colors.neutral[500] }}>
                        (Optional)
                      </span>
                    </h3>
                    <TextAreaField
                      height={100}
                      data-cy="confidential-feedback-textarea"
                      style={{ width: 468 }}
                      placeholder="Enter information"
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                    />
                    <div style={{ margin: '35px 0px' }}>
                      <Button
                        size="lg"
                        onClick={handleSendClick}
                        data-cy="job-summary-rating-send-button"
                        disabled={isButtonDisabled}
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showUnableToAddBlockedModal && (
        <ConfirmPopup
          title="Unable to Add to Favorites"
          description="To add this professional to your Favorites, please contact Customer Support to remove this professional from your blocked list."
          rightButtonText="Close"
          rightButtonAction={() => setShowUnableToAddBlockedModal(false)}
        />
      )}

      {showBlockConfirmPopup && (
        <ConfirmPopup
          title="Block Professional"
          description={
            isFavorite
              ? // eslint-disable-next-line max-len
                `To block this professional, they must be removed from your Favorites list. Would you like to continue with removing ${professionalName} from your Favorites and blocking them?`
              : 'Are you sure you want to block this professional from working with you again?'
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setShowBlockConfirmPopup(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            dispatch(blockProfessional({ user: job.offer_final_reciever }));
          }}
          Icon={() => <OutlineXCircleErrorIcon />}
          closePopup={() => setShowBlockConfirmPopup(false)}
        />
      )}

      <RatingFeedbackSuccessHandler
        averageRating={averageRating as number}
        job={job}
        isTriggered={shouldShowHandler}
        onSubmitFeedback={sendFeedback}
        shouldRestorePopup={showEodInvitePopup}
      />
    </>
  );
};

export default JobSummaryRating;
